/**
 * Scarf upsell card in the cart drawer (snippets/kz-scarf-offer.liquid).
 *
 * Adds the selected scarf through Shopify.actions.updateCart, the same standard action the
 * theme configures in standard-actions-override.js. It emits the cart lines-update event,
 * so cart-items-component re-renders the drawer section (the card disappears and the scarf
 * line shows the applied discount) and the cart icon count updates, all without a reload.
 */
if (!customElements.get('kz-scarf-offer')) {
  class KzScarfOffer extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', this.#handleClick);
      // For kasumee-analytics.js (seen_scarf_offer)
      this.dispatchEvent(new Event('kz-scarf-offer:connected', { bubbles: true }));
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.#handleClick);
    }

    /** @param {MouseEvent} event */
    #handleClick = (event) => {
      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;

      const swatch = target.closest('[data-kz-variant]');
      if (swatch instanceof HTMLElement) {
        this.#select(swatch);
        return;
      }

      const addButton = target.closest('[data-kz-add]');
      if (addButton instanceof HTMLButtonElement) this.#add(addButton);
    };

    /** @param {HTMLElement} swatch */
    #select(swatch) {
      // Swatches keep their data in data-kz-variant-* so they never match the [data-kz-price] targets
      const {
        kzVariant,
        kzVariantTitle: kzTitle,
        kzVariantPrice: kzPrice,
        kzVariantCompare: kzCompare,
        kzVariantSrc: kzSrc,
        kzVariantAlt: kzAlt,
      } = swatch.dataset;
      if (!kzVariant) return;

      this.dataset.variantId = kzVariant;
      for (const other of this.querySelectorAll('[data-kz-variant]')) {
        other.setAttribute('aria-checked', String(other === swatch));
      }

      const color = this.querySelector('[data-kz-color]');
      if (color && kzTitle) color.textContent = kzTitle;
      if (kzPrice) this.querySelectorAll('[data-kz-price]').forEach((el) => (el.textContent = kzPrice));
      const compare = this.querySelector('[data-kz-compare]');
      if (compare && kzCompare) compare.textContent = kzCompare;

      const image = this.querySelector('[data-kz-image]');
      if (image instanceof HTMLImageElement && kzSrc) {
        image.removeAttribute('srcset');
        image.src = kzSrc;
        image.alt = kzAlt || '';
      }
    }

    /** @param {HTMLButtonElement} button */
    async #add(button) {
      const variantId = this.dataset.variantId;
      if (!variantId || button.disabled) return;

      button.disabled = true;
      button.setAttribute('aria-busy', 'true');

      try {
        const updateCart = window.Shopify?.actions?.updateCart;
        if (updateCart) {
          const result = await updateCart({ lines: [{ merchandiseId: variantId, quantity: 1 }] });
          if (result?.userErrors?.length) throw new Error(result.userErrors[0].message);
        } else {
          // No standard actions on this page: add via the AJAX API and reload to show the cart.
          const response = await fetch(`${window.Shopify?.routes?.root ?? '/'}cart/add.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] }),
          });
          if (!response.ok) throw new Error(`Add to cart failed: ${response.status}`);
          window.location.reload();
          return;
        }

        // The section re-render replaces this card; until then, confirm the add.
        button.textContent = button.dataset.kzAddedLabel || 'Added ✓';
      } catch (error) {
        console.warn('[kz-scarf-offer]', error);
        button.disabled = false;
      } finally {
        button.removeAttribute('aria-busy');
      }
    }
  }

  customElements.define('kz-scarf-offer', KzScarfOffer);
}
